import Cookies from "universal-cookie";
import { CookieSetOptions } from "universal-cookie/cjs/types";
import { AppContextProps } from "../../libs/app-core/AppContext";
import { Toast } from "../../libs/app-core/Toast";
import { API } from "../dao/API";
import { UserInfoDTO } from "../dao/DTOs";
import { EmpDAO } from "../dao/EmpDAO";
import { MemberDAO } from "../dao/MemberDAO";
import { new_ErrorAlert } from "../export_spwk/ExportManager";
import { GVContextProps } from "../GV";
import { NavigateToFirstPage_Asnyc } from "../pages/FrmMemberLogin";
import { NavigateTo_FrmMemberTodo_Async } from "../pages/FrmProjectEditor";
import {
  ROUTE_admin_login,
  ROUTE_member_login,
  ROUTE_project_list,
} from "../Routes";
import i18n from "../../i18n";

export enum HeadersEnum {
  ContentType = "Content-Type",
  Authorization = "Authorization",
  Accept = "Accept",
}

export enum HeaderValueEnum {
  ApplicationJson = "application/json",
  Bearer = "Bearer",
}

function toLowerCaseAddress(key: string) {
  return key.indexOf("0x") >= 0 ? key.toLowerCase() : key;
}

class AuthHelper {
  static getAuthorizationFromHeader = (headers: Headers): string => {
    const bearer =
      headers.get(HeadersEnum.Authorization) ||
      headers.get(HeadersEnum.Authorization.toLowerCase()) ||
      `${HeaderValueEnum.Bearer} `;
    return bearer?.slice(7);
  };

  static saveJwtToCookie(key: string, jwt: string, seconds?: number): void {
    const cookies = new Cookies();
    const age = seconds ? seconds : 2592000; // 60 * 60 * 24 * 30; // 30 day

    cookies.set(toLowerCaseAddress(key), jwt, {
      path: "/",
      maxAge: age,
    } as CookieSetOptions);

    return;
  }

  static getJwtFromCookie(key: string): string {
    const cookies = new Cookies();
    return cookies.get(toLowerCaseAddress(key));
  }

  static getJWTPayload(key: string): UserInfoDTO | null {
    const jwt = this.getJwtFromCookie(key);
    if (jwt) {
      const payload = JSON.parse(
        Buffer.from(jwt.split(".")[1], "base64").toString()
      );
      return payload as UserInfoDTO;
    } else {
      return null;
    }
  }

  static getAuthorizationBearer(key: string): string {
    const cookies = new Cookies();
    const value = cookies.get(toLowerCaseAddress(key));
    if (typeof value == "undefined" || !value) {
      return "";
    }
    return `${HeaderValueEnum.Bearer} ${value}`;
  }

  static clearCookie(key: string): void {
    const cookies = new Cookies();
    cookies.set(toLowerCaseAddress(key), "", {
      path: "/",
      maxAge: 1,
    } as CookieSetOptions);
    cookies.remove(toLowerCaseAddress(key));
    return;
  }

  // 以下為具體的 登入 與 登出 ------------------------------------------------------
  // 先暫時把 登入 登出 集中於此，避免發散
  // 等確定登入 與 額外儲存的資訊穩定再拆出

  static async EmpLogin(
    GV: GVContextProps,
    ctx: AppContextProps,
    id: string,
    pw: string
  ) {
    localStorage.clear(); // 登入前總是清除
    AuthHelper.clearCookie(API.BearerKey); // 不管登入成功或失敗，之前的都視為登出

    if (id == null || id === "" || pw == null || pw === "") {
      Toast.ShowWarning(ctx, "帳號/密碼 不可空白 !");
      return;
    }

    try {
      const user = await EmpDAO.Login(id, pw);

      if (!user) {
        Toast.ShowDanger(ctx, "帳號或密碼錯誤!");
        return;
      }

      GV.setUser(user);
      ctx.Navigate(ROUTE_project_list.url);
      Toast.ShowScuccess(ctx, "登入成功: " + user?.username);
    } catch (error) {
      console.log(error);
      alert("未知的錯誤");
    }
  }

  static async MemberLogin(
    GV: GVContextProps,
    ctx: AppContextProps,
    cid: string | null | undefined,
    username: string,
    password: string
  ) {
    localStorage.clear(); // 登入前總是清除
    AuthHelper.clearCookie(API.BearerKey); // 不管登入成功或失敗，之前的都視為登出
    if (
      username == null ||
      username === "" ||
      password == null ||
      password === ""
    ) {
      Toast.ShowWarning(ctx, i18n.t("帳號/密碼 不可空白 !"));
      return;
    }

    if (cid == null || cid === "") {
      Toast.ShowWarning(ctx, i18n.t("場次代碼 不可空白 !"));
      return;
    }

    try {
      const re = await MemberDAO.Login(cid, username, password);
      if (!re) {
        Toast.ShowDanger(ctx, i18n.t("帳號或密碼錯誤!"));
        return;
      }
      if (re.code === 5000) {
        Toast.ShowDanger(ctx, i18n.t(re.message));
        return;
      }
      let user = re.data as UserInfoDTO;
      GV.setUser(user);

      /* 非機密資料 */
      localStorage.setItem("cid", cid); // 場次代碼

      Toast.ShowScuccess(ctx, i18n.t("登入成功: ") + user?.username);

      let member_guid = user.id;
      const IsNewVersion = true;
      if (IsNewVersion) {
        //#region 新版本:登入時前往主選單
        await NavigateTo_FrmMemberTodo_Async(ctx, member_guid);
        //#endregion
      } else if (IsNewVersion === false) {
        //#region 舊版本: 登入時直接前往表單
        await NavigateToFirstPage_Asnyc(ctx, member_guid); // 移到第一個畫面，實際上移到哪裡，先由 FrmMemberLogin 內部決定
        //#endregion
      } else {
        throw new_ErrorAlert("7943186 check IsNewVersion");
      }
    } catch (ex: any) {
      console.log((ex as Error).message);
      Toast.ShowDanger(ctx, i18n.t("驗證時發生問題!"));
    }
  }

  static Logout(GV: GVContextProps, ctx: AppContextProps) {
    AuthHelper.clearCookie(API.BearerKey);

    let cid = localStorage.getItem("cid") ?? ""; // 場次代碼

    // 清除使用者資料
    GV.setUser(null);
    localStorage.clear();

    const redirectTo = GV.IsAdmin()
      ? ROUTE_admin_login.url
      : ROUTE_member_login.url + `?cid=${cid}`;
    ctx.Navigate(redirectTo);

    // 導向至登入頁面？
    Toast.Show(ctx, "登出");
  }
}

export default AuthHelper;
